import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { UserDto } from 'continity-shared-ui';
import { ShellConfigService } from './shell-config.service';
import { TokenStorageService } from './token-storage.service';
import { AUTH_SESSION_GATEWAY, AuthSessionGateway } from './auth-session-gateway';
import { AuthBroadcastService } from './auth-broadcast.service';

interface AuthStatusResponse {
  hasRefreshCookie: boolean;
  isAuthenticated?: boolean;
}

interface TokenResponse {
  accessToken: string;
  expiresIn: number;
}

/**
 * Lean auth layer for the Continity shell apps. Each portal host owns its HttpOnly
 * refresh cookie; the short-lived bearer is fetched via `POST /Auth/refresh` and held
 * in memory. Cross-portal B2C SSO comes from the B2C identity-provider session: the
 * second host runs its own authorization-code flow without asking for credentials again
 * (same B2C app-registration / tenant — PBI 1463 AC).
 *
 * Endpoints mirrored from the main portal's AuthHttpService:
 *   GET  /Auth/status   — does a refresh cookie exist?
 *   POST /Auth/refresh  — exchange the cookie for a bearer
 *   GET  /Auth/me       — current user
 *   GET  /Auth/roles    — role claims (drives app-specific access checks)
 *
 * SCOPE — this is a SESSION-PROBE only. It covers status / refresh / me / roles
 * against an ALREADY-established cookie session. It deliberately does NOT own:
 *   - login INITIATION (redirect to B2C) and LOGOUT → app-supplied via the optional
 *     {@link AUTH_SESSION_GATEWAY} (see {@link initiateLogin} / {@link logout}),
 *   - 401/403 handling → app-supplied via the optional `AUTH_ERROR_HANDLER`
 *     consumed by the shell `authInterceptor`.
 * No B2C/PKCE is implemented here (ties to PBI 1466 — dedicated Phoenix B2C
 * app-registration). With no gateway provided, this shell instance is probe-only.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  /** Emits true once the initial auth probe has completed. */
  public authReady$ = new BehaviorSubject<boolean>(false);

  private userSubject = new BehaviorSubject<UserDto | null>(null);
  public currentUser$ = this.userSubject.asObservable();

  private rolesCache: string[] | null = null;

  private authInitPromise: Promise<void>;
  private resolveAuthInit!: () => void;

  constructor(
    private http: HttpClient,
    private config: ShellConfigService,
    private tokenStorage: TokenStorageService,
    private authBroadcast: AuthBroadcastService,
    @Optional()
    @Inject(AUTH_SESSION_GATEWAY)
    private sessionGateway: AuthSessionGateway | null = null
  ) {
    this.authInitPromise = new Promise((resolve) => (this.resolveAuthInit = resolve));
    this.authBroadcast.logout$.subscribe(() => this.clearLocalSession());
    // The initial auth probe is kicked off by the APP_INITIALIZER (provideShell in the
    // library) AFTER the runtime config has resolved — NOT from the constructor.
    // checkAuthState() reads config.apiUrl, so it must not run before loadConfig()
    // completes, otherwise it would fall back to the SHELL_CONFIG default in deploy
    // environments where config.json is injected at runtime.
  }

  /** Kicks off the initial cookie → bearer → user probe. Must be called only after
   *  ShellConfigService.loadConfig() has resolved (see provideShell initializer). */
  initAuth(): void {
    this.checkAuthState();
  }

  private get apiUrl(): string {
    return this.config.apiUrl;
  }

  get isAuthenticated(): boolean {
    return this.tokenStorage.isTokenValid;
  }

  get currentUser(): UserDto | null {
    return this.userSubject.value;
  }

  waitForAuthInit(): Promise<void> {
    return this.authInitPromise;
  }

  getToken(): string | null {
    return this.tokenStorage.getToken();
  }

  private skipInterceptorHeaders(): HttpHeaders {
    return new HttpHeaders().set('X-Skip-Interceptor', 'true');
  }

  /** On startup: if a refresh cookie exists, exchange it for a bearer and load the user. */
  private checkAuthState(): void {
    this.http
      .get<AuthStatusResponse>(`${this.apiUrl}/Auth/status`, {
        withCredentials: true,
        headers: this.skipInterceptorHeaders(),
      })
      .pipe(
        switchMap((status) => {
          if (!status?.hasRefreshCookie) return of(null);
          return this.refreshToken();
        }),
        switchMap((token) => {
          if (!token) return of(null);
          return this.loadUser();
        }),
        catchError(() => of(null)),
        tap(() => {
          this.authReady$.next(true);
          this.resolveAuthInit();
        })
      )
      .subscribe();
  }

  refreshToken(): Observable<TokenResponse | null> {
    return this.http
      .post<TokenResponse>(
        `${this.apiUrl}/Auth/refresh`,
        {},
        { withCredentials: true, headers: this.skipInterceptorHeaders() }
      )
      .pipe(
        tap((res) => {
          if (res?.accessToken) {
            this.tokenStorage.setToken(res.accessToken, res.expiresIn);
          }
        }),
        catchError(() => of(null))
      );
  }

  loadUser(): Observable<UserDto | null> {
    return this.http.get<UserDto>(`${this.apiUrl}/Auth/me`).pipe(
      tap((user) => this.userSubject.next(user)),
      catchError(() => of(null))
    );
  }

  getUserRoles(): Observable<string[]> {
    if (this.rolesCache) return of(this.rolesCache);
    if (!this.isAuthenticated) return of([]);
    return this.http.get<string[]>(`${this.apiUrl}/Auth/roles`).pipe(
      tap((roles) => (this.rolesCache = roles)),
      catchError(() => of([]))
    );
  }

  clearRolesCache(): void {
    this.rolesCache = null;
  }

  /**
   * Begin an interactive login. Delegates to the app-supplied
   * {@link AUTH_SESSION_GATEWAY}; if none is provided this is a no-op (the shell
   * AuthService is session-probe-only and does not implement B2C/PKCE — PBI 1466).
   */
  initiateLogin(returnUrl?: string): void {
    if (this.sessionGateway?.initiateLogin) {
      this.sessionGateway.initiateLogin(returnUrl);
      return;
    }
    console.warn(
      '[continity-shared-shell] AuthService.initiateLogin called but no AUTH_SESSION_GATEWAY provided; this shell instance is session-probe-only.'
    );
  }

  /**
   * End the session. Delegates to the app-supplied {@link AUTH_SESSION_GATEWAY};
   * if none is provided this is a no-op (see {@link initiateLogin}).
   */
  logout(): void {
    this.authBroadcast.broadcastLogout();
    this.clearLocalSession();
    if (this.sessionGateway?.logout) {
      this.sessionGateway.logout();
      return;
    }
    console.warn(
      '[continity-shared-shell] AuthService.logout called but no AUTH_SESSION_GATEWAY provided; this shell instance is session-probe-only.'
    );
  }

  private clearLocalSession(): void {
    this.tokenStorage.clearToken();
    this.userSubject.next(null);
    this.rolesCache = null;
  }
}
